import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { StepHandle, StepProps } from '../BusinessProfileSetupPage';
import { businessProfileSetupGateway } from '../../services/BusinessProfileSetupGateway';
import { accountNumberDigitsFromInput, routingNumberDigitsFromInput } from '../../../../shared/formatting';
import { routingChecksumValid } from '../../validation';
import { ApiError } from '../../../identity/types';
import type { FundingAccountResponse } from '../../types';

const ACCOUNT_TYPES = ['Checking', 'Savings'];

interface FormState {
  bankName: string;
  accountType: string;
  routingNumber: string;
  accountNumber: string;
}

const EMPTY: FormState = { bankName: '', accountType: '', routingNumber: '', accountNumber: '' };

function validate(form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.bankName.trim().length < 2) errors.bankName = 'Enter the bank name.';
  if (!form.accountType) errors.accountType = 'Select an account type.';
  if (!/^\d{9}$/.test(form.routingNumber) || !routingChecksumValid(form.routingNumber)) {
    errors.routingNumber = 'Invalid ABA routing number. Please verify with your check.';
  }
  if (!/^\d{4,17}$/.test(form.accountNumber)) errors.accountNumber = 'Enter 4–17 digits for the bank account number.';
  return errors;
}

export const BankingStep = forwardRef<StepHandle, StepProps>(function BankingStep({ markStepComplete, readOnly, mode }, ref) {
  const [existing, setExisting] = useState<FundingAccountResponse | null>(null);
  // setup mode only — the original v9 "click Update Bank Information to re-edit an already-saved account"
  // sub-toggle, preserved unchanged for that mode (see this component's own EPN-37 unified-edit-entry-point
  // correction notes below). admin-edit no longer reads this for its own render — its equivalent state is
  // simply `!readOnly`, driven entirely by the page-level Edit Company Details action.
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The account number is masked while typing (password-style) and can be revealed on request; it is hidden again whenever the field is re-opened.
  const [showAccountNumber, setShowAccountNumber] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const account = await businessProfileSetupGateway.getFundingAccount();
        if (cancelled) return;
        setExisting(account);
        setEditing(mode === 'setup' ? !account : true);
        if (!account) {
          // No canonical funding account yet — restore any non-sensitive Step 3 draft (BankName/
          // AccountType only; Routing/Account Number are never staged in a draft and always need
          // re-entry — see SaveFundingAccountDraftRequest's own doc comment).
          const draft = await businessProfileSetupGateway.getFundingAccountDraft();
          if (!cancelled && draft) {
            setForm((prev) => ({ ...prev, bankName: draft.bankName ?? '', accountType: draft.accountType ?? '' }));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `mode` is fixed for the lifetime of a mounted step instance (this component isn't re-parented
    // between modes) — intentionally read once on mount, exactly like every other step's own mount effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field: keyof FormState, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const saveIfValid = async (): Promise<boolean> => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) return false;

    setApiError(null);
    const saved = await businessProfileSetupGateway.saveFundingAccount({
      bankName: form.bankName.trim(),
      accountType: form.accountType,
      routingNumber: form.routingNumber,
      accountNumber: form.accountNumber,
    });
    setExisting(saved);
    setEditing(false);
    return true;
  };

  const formUntouched = !form.bankName && !form.accountType && !form.routingNumber && !form.accountNumber;

  /** Shared by saveAndContinue (setup mode) and commitEdits (admin-edit's Save Changes). When there is
   * nothing new to validate or persist — matches every other step's Save Changes no-op-when-untouched
   * behavior:
   * - setup mode: the read-only summary is still showing (the admin never clicked "Update Bank
   *   Information" for this section) — the original `!editing` check.
   * - admin-edit: an account already exists and the (always-blank-until-typed-into, see EPN-37
   *   unified-edit-entry-point correction notes above) form was never touched — there's no more separate
   *   `editing` sub-toggle to check here, since Edit Company Details alone reveals the form. */
  const commit = async () => {
    if (mode === 'setup' ? !editing && existing : existing && formUntouched) {
      markStepComplete('banking');
      return;
    }

    const validationErrors = validate(form);
    setErrors(validationErrors);
    setShowErrors(true);
    if (Object.keys(validationErrors).length > 0) {
      throw new Error('validation failed');
    }

    try {
      await saveIfValid();
      markStepComplete('banking');
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not save your bank information. Please try again.');
      throw err;
    }
  };

  useImperativeHandle(ref, () => ({
    saveAndContinue: commit,
    commitEdits: commit,
    // EPN-37 unsaved-changes correction: Bank Information's own 4 fields are ALWAYS blank on entry into
    // Edit mode (see this component's own EPN-37 unified-edit-entry-point correction notes — sensitive
    // values are never repopulated from persisted masked data) — so there is no "loaded baseline" to diff
    // against the way BasicInfoStep/AddressStep/PayrollSetupStep do. Dirty here simply means: at least one
    // of those blank fields now holds a value the admin typed. In setup mode this only applies once the
    // account is actually being (re-)entered (`editing`) — the read-only summary view has nothing unsaved.
    isDirty: () => (mode === 'setup' ? editing && !formUntouched : !formUntouched),
    async saveAndExit() {
      // Save and Exit never promotes to the canonical funding account, even when the entry is already
      // fully valid — only Save and Continue does that, after its own validation. This always persists
      // only the permitted non-sensitive Step 3 draft metadata (BankName/AccountType); Routing/Account
      // Number are never staged in a draft (see SaveFundingAccountDraftRequest's own doc comment) and
      // must always be re-entered on resume, however complete the rest of the entry was.
      if (!editing) return;
      setApiError(null);
      setNotice(null);
      try {
        await businessProfileSetupGateway.saveFundingAccountDraft({
          bankName: form.bankName.trim() || null,
          accountType: form.accountType || null,
        });
        setNotice('Bank Name and Account Type were saved. For security, Routing Number and Account Number are never saved as a draft — please re-enter them when you resume.');
      } catch (err) {
        setApiError(err instanceof ApiError ? err.message : 'Could not save your bank information. Please try again.');
      }
    },
  }));

  if (loading) return <p className="helper">Loading…</p>;

  // EPN-37 unified-edit-entry-point correction: in admin-edit, "Edit Company Details" (the page-level
  // header action) is the ONLY way any section becomes editable — Bank Information must behave exactly
  // like every other section, with no separate secondary button of its own. setup mode keeps its original
  // "view the saved account, click Update Bank Information to re-edit it" two-step flow unchanged (see the
  // mount effect and `commit`'s own comments above for why `editing` still drives that mode's behavior).
  const showSummary = mode === 'admin-edit' ? readOnly && !!existing : !editing && !!existing;
  const showUpdateButton = mode === 'setup' && !editing && !!existing;

  return (
    <div>
      {showSummary && existing && (
        <div className="bp-grid">
          <div className="bp-form-group">
            <label>Bank Name</label>
            <div className="bank-derived">{existing.bankName}</div>
          </div>
          <div className="bp-form-group">
            <label>Account Type</label>
            <div className="bank-derived">{existing.accountType}</div>
          </div>
          <div className="bp-form-group">
            <label>Bank Account Number</label>
            <div className="bank-derived">•••• {existing.maskedAccountLast4}</div>
          </div>
          <div className="bp-form-group">
            <label>Verification Status</label>
            <div className="bank-derived">{existing.verificationState}</div>
          </div>
          {showUpdateButton && (
            <div className="bp-form-group span-2">
              <button type="button" className="bp-btn bp-btn-secondary" onClick={() => setEditing(true)}>
                <i className="fa fa-pencil bps-icon" aria-hidden="true" />
                Update Bank Information
              </button>
            </div>
          )}
        </div>
      )}

      {!existing && readOnly && <p className="helper">No bank information on file yet.</p>}

      {editing && !readOnly && (
        <div className="bp-grid">
          <div className="bp-form-group">
            <label htmlFor="bps-bankName">
              Bank Name <span className="req">*</span>
            </label>
            <input
              id="bps-bankName"
              className={`bp-input${showErrors && errors.bankName ? ' invalid' : ''}`}
              value={form.bankName}
              maxLength={80}
              onChange={(e) => set('bankName', e.target.value)}
            />
            {showErrors && errors.bankName && <div className="error">{errors.bankName}</div>}
          </div>
          <div className="bp-form-group">
            <label htmlFor="bps-routing">
              Bank Routing Number <span className="req">*</span>
            </label>
            <input
              id="bps-routing"
              className={`bp-input${showErrors && errors.routingNumber ? ' invalid' : ''}`}
              value={form.routingNumber}
              maxLength={9}
              inputMode="numeric"
              onChange={(e) => set('routingNumber', routingNumberDigitsFromInput(e.target.value))}
            />
            {showErrors && errors.routingNumber && <div className="error">{errors.routingNumber}</div>}
          </div>
          <div className="bp-form-group">
            <label htmlFor="bps-accountType">
              Account Type <span className="req">*</span>
            </label>
            <select id="bps-accountType" className={`bp-select${showErrors && errors.accountType ? ' invalid' : ''}`} value={form.accountType} onChange={(e) => set('accountType', e.target.value)}>
              <option value="">-- Select --</option>
              {ACCOUNT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {showErrors && errors.accountType && <div className="error">{errors.accountType}</div>}
          </div>
          <div className="bp-form-group">
            <label htmlFor="bps-accountNumber">
              Bank Account Number <span className="req">*</span>
            </label>
            <div className="bp-input-wrap">
              <input
                id="bps-accountNumber"
                className={`bp-input has-toggle${showErrors && errors.accountNumber ? ' invalid' : ''}`}
                type={showAccountNumber ? 'text' : 'password'}
                autoComplete="off"
                value={form.accountNumber}
                maxLength={17}
                inputMode="numeric"
                onChange={(e) => set('accountNumber', accountNumberDigitsFromInput(e.target.value))}
              />
              <button
                type="button"
                className="bp-input-toggle"
                aria-label={showAccountNumber ? 'Hide digits' : 'Show digits'}
                aria-pressed={showAccountNumber}
                onClick={() => setShowAccountNumber((v) => !v)}
              >
                <i className={`fa ${showAccountNumber ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
              </button>
            </div>
            {showErrors && errors.accountNumber && <div className="error">{errors.accountNumber}</div>}
          </div>
        </div>
      )}

      {notice && <div className="notice">{notice}</div>}
      {apiError && <div className="notice" style={{ color: 'var(--bps-danger)' }}>{apiError}</div>}
    </div>
  );
});
